import cn from "classnames";
import { useTranslation } from "react-i18next";

import InfoIcon from "assets/icons/info-new.svg?react";

import { CALCULATOR_SCENARIO_VARIANT } from "constants/calculator";

import { convertValueToLocale } from "helpers/convertValueToLocale";

import { Popover } from "shared/ui/components/Popover";
import { Select } from "shared/ui/components/Select";
import { Typography } from "shared/ui/components/Typography";

import { getChartData } from "./CalculatedData.helpers";

import styles from "./CalculatedData.module.scss";

const CalculatedData = ({
  activePosition,
  proCalculationCharts,
  calculation,
  conclusionsPro,
  onPositionChange,
}: {
  activePosition: 10 | 20 | 30;
  proCalculationCharts: any;
  calculation: any;
  conclusionsPro: any;
  onPositionChange: (position: 10 | 20 | 30) => void;
}) => {
  const { t } = useTranslation();

  const {
    earlySaleProfit,
    rentalYield,
    irr,
    roi,
    investmentsAcc,
    investorCashFlowAverage,
    specialLoan,
    totalTaxSavings,
    userScenarioResult,
    rentalYieldVariant,
  } = getChartData({
    proCalculationCharts,
    calculation,
    activePosition,
    conclusionsPro,
  });

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography variant="bodyM" weight="semiBold" className={styles.title}>
          {t("profitability.title")}
        </Typography>
        <Select
          className={styles.select}
          controlClassName={styles.selectControl}
          dropdownIndicatorClassName={styles.selectDropdownIndicator}
          hideSelectedOptions={false}
          blockScrollOnMenu={false}
          options={[
            { value: 10, label: `10 ${t("my_parameters.parameters.years")}` },
            { value: 20, label: `20 ${t("my_parameters.parameters.years")}` },
            { value: 30, label: `30 ${t("my_parameters.parameters.years")}` },
          ]}
          value={{
            value: activePosition,
            label: `${activePosition} ${t("my_parameters.parameters.years")}`,
          }}
          valueRenderer={({ data }) => (
            <Typography
              className={cn(styles.selectControlTitle, styles.title)}
              variant="bodyM"
              weight="semiBold"
            >
              {data}
            </Typography>
          )}
          optionRenderer={({ data, innerProps, isSelected }) => {
            return (
              <Typography
                onClick={innerProps.onClick}
                className={cn(styles.selectOption, {
                  [styles.selectOptionActive]: isSelected,
                })}
                variant="bodyS"
                weight="semiBold"
              >
                {data}
              </Typography>
            );
          }}
          onChange={({ value }) => onPositionChange(value)}
        />
      </div>
      <div className={styles.calculatedValuesWrapper}>
        {!!earlySaleProfit && (
          <div className={styles.chartListItem}>
            <Typography
              className={styles.chartListText}
              variant="bodyS"
              weight="regular"
            >
              {t("calculator_pro_page.analytics.early_sale_profit")}
            </Typography>

            <div className={styles.chartListInner}>
              <Typography
                data-testid={"earlySaleProfit"}
                className={cn(styles.chartListValue, {
                  [styles.chartListValuePositive]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE,
                  [styles.chartListValueNeutral]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEUTRAL,
                  [styles.chartListValueNegative]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEGATIVE,
                })}
                variant="numbers"
                weight="medium"
              >
                {convertValueToLocale(earlySaleProfit, 0)}
              </Typography>

              <Popover
                placement="top"
                triggerType="hover"
                yOffset={15}
                floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
                triggerClassName={styles.trigger}
                triggerElement={() => (
                  <InfoIcon className={styles.chartListIcon} />
                )}
              >
                <div className={styles.chartListPopover}>
                  <Typography variant="bodyS" weight="medium">
                    {t(
                      "calculator_pro_page.analytics.early_sale_profit_tooltip"
                    )}
                  </Typography>
                </div>
              </Popover>
            </div>
          </div>
        )}
        {!!rentalYield && (
          <div className={styles.chartListItem}>
            <Typography
              className={styles.chartListText}
              variant="bodyS"
              weight="regular"
            >
              {t("calculator_pro_page.analytics.rental_yield")}
            </Typography>

            <div className={styles.chartListInner}>
              <Typography
                data-testid={"rentalYield"}
                className={cn(styles.chartListValue, {
                  [styles.chartListValuePositive]:
                    rentalYieldVariant === CALCULATOR_SCENARIO_VARIANT.POSITIVE,
                  [styles.chartListValueNeutral]:
                    rentalYieldVariant === CALCULATOR_SCENARIO_VARIANT.NEUTRAL,
                  [styles.chartListValueNegative]:
                    rentalYieldVariant === CALCULATOR_SCENARIO_VARIANT.NEGATIVE,
                })}
                variant="numbers"
                weight="medium"
              >
                {convertValueToLocale(rentalYield, 2, "%")}
              </Typography>

              <Popover
                placement="top"
                triggerType="hover"
                yOffset={15}
                floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
                triggerClassName={styles.trigger}
                triggerElement={() => (
                  <InfoIcon className={styles.chartListIcon} />
                )}
              >
                <div className={styles.chartListPopover}>
                  <Typography variant="bodyS" weight="medium">
                    {t("calculator_pro_page.analytics.rental_yield_tooltip_1")}
                  </Typography>

                  <Typography variant="bodyS" weight="medium">
                    {t("calculator_pro_page.analytics.rental_yield_tooltip_2")}
                  </Typography>

                  <Typography variant="bodyS" weight="medium">
                    {t("calculator_pro_page.analytics.rental_yield_tooltip_3")}
                  </Typography>
                </div>
              </Popover>
            </div>
          </div>
        )}
        {!!irr && (
          <div className={styles.chartListItem}>
            <Typography
              className={styles.chartListText}
              variant="bodyS"
              weight="regular"
            >
              {t("calculator_pro_page.analytics.irr")}
            </Typography>

            <div className={styles.chartListInner}>
              <Typography
                data-testid={"irr"}
                className={cn(styles.chartListValue, {
                  [styles.chartListValuePositive]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE,
                  [styles.chartListValueNeutral]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEUTRAL,
                  [styles.chartListValueNegative]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEGATIVE,
                })}
                variant="numbers"
                weight="medium"
              >
                {convertValueToLocale(irr, 2, "%")}
              </Typography>

              <Popover
                placement="top"
                triggerType="hover"
                yOffset={15}
                floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
                triggerClassName={styles.trigger}
                triggerElement={() => (
                  <InfoIcon className={styles.chartListIcon} />
                )}
              >
                <div className={styles.chartListPopover}>
                  <Typography variant="bodyS" weight="medium">
                    {t("calculator_pro_page.analytics.irr_tooltip_1")}
                  </Typography>

                  <Typography variant="bodyS" weight="medium">
                    {t("calculator_pro_page.analytics.irr_tooltip_2")}
                  </Typography>

                  <Typography variant="bodyS" weight="medium">
                    {t("calculator_pro_page.analytics.irr_tooltip_3")}
                  </Typography>
                </div>
              </Popover>
            </div>
          </div>
        )}
        {!!roi && (
          <div className={styles.chartListItem}>
            <Typography
              className={styles.chartListText}
              variant="bodyS"
              weight="regular"
            >
              {t("calculator_pro_page.analytics.roi")}
            </Typography>

            <div className={styles.chartListInner}>
              <Typography
                data-testid={"roi"}
                className={cn(styles.chartListValue, {
                  [styles.chartListValuePositive]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE,
                  [styles.chartListValueNeutral]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEUTRAL,
                  [styles.chartListValueNegative]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEGATIVE,
                })}
                variant="numbers"
                weight="medium"
              >
                {convertValueToLocale(roi, 2, "%")}
              </Typography>

              <Popover
                placement="top"
                triggerType="hover"
                yOffset={15}
                floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
                triggerClassName={styles.trigger}
                triggerElement={() => (
                  <InfoIcon className={styles.chartListIcon} />
                )}
              >
                <div className={styles.chartListPopover}>
                  <Typography variant="bodyS" weight="medium">
                    {t("calculator_pro_page.analytics.roi_tooltip_1")}
                  </Typography>

                  <Typography variant="bodyS" weight="medium">
                    {t("calculator_pro_page.analytics.roi_tooltip_2")}
                  </Typography>

                  <Typography variant="bodyS" weight="medium">
                    {t("calculator_pro_page.analytics.roi_tooltip_3")}
                  </Typography>

                  <Typography variant="bodyS" weight="medium">
                    {t("calculator_pro_page.analytics.roi_tooltip_4")}
                  </Typography>

                  <Typography variant="bodyS" weight="medium">
                    {t("calculator_pro_page.analytics.roi_tooltip_5")}
                  </Typography>

                  <Typography variant="bodyS" weight="medium">
                    {t("calculator_pro_page.analytics.roi_tooltip_6")}
                  </Typography>
                </div>
              </Popover>
            </div>
          </div>
        )}
        {!!investmentsAcc && (
          <div className={styles.chartListItem}>
            <Typography
              className={styles.chartListText}
              variant="bodyS"
              weight="regular"
            >
              {t("calculator_pro_page.analytics.investments_acc")}
            </Typography>

            <div className={styles.chartListInner}>
              <Typography
                data-testid={"investmentsAcc"}
                className={cn(styles.chartListValue, {
                  [styles.chartListValuePositive]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE,
                  [styles.chartListValueNeutral]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEUTRAL,
                  [styles.chartListValueNegative]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEGATIVE,
                })}
                variant="numbers"
                weight="medium"
              >
                {convertValueToLocale(Math.abs(investmentsAcc), 0)}
              </Typography>

              <Popover
                placement="top"
                triggerType="hover"
                yOffset={15}
                floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
                triggerClassName={styles.trigger}
                triggerElement={() => (
                  <InfoIcon className={styles.chartListIcon} />
                )}
              >
                <div className={styles.chartListPopover}>
                  <Typography variant="bodyS" weight="medium">
                    {t("calculator_pro_page.analytics.investments_acc_tooltip")}
                  </Typography>
                </div>
              </Popover>
            </div>
          </div>
        )}
        {!!investorCashFlowAverage && (
          <div className={styles.chartListItem}>
            <Typography
              className={styles.chartListText}
              variant="bodyS"
              weight="regular"
            >
              {t("calculator_pro_page.analytics.investor_cash_flow_average")}
            </Typography>

            <div className={styles.chartListInner}>
              <Typography
                data-testid={"investorCashFlowAverage"}
                className={cn(styles.chartListValue, {
                  [styles.chartListValuePositive]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE,
                  [styles.chartListValueNeutral]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEUTRAL,
                  [styles.chartListValueNegative]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEGATIVE,
                })}
                variant="numbers"
                weight="medium"
              >
                {convertValueToLocale(investorCashFlowAverage)}
              </Typography>

              <Popover
                placement="top"
                triggerType="hover"
                yOffset={15}
                floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
                triggerClassName={styles.trigger}
                triggerElement={() => (
                  <InfoIcon className={styles.chartListIcon} />
                )}
              >
                <div className={styles.chartListPopover}>
                  <Typography variant="bodyS" weight="medium">
                    {t(
                      "calculator_pro_page.analytics.investor_cash_flow_average_tooltip"
                    )}
                  </Typography>
                </div>
              </Popover>
            </div>
          </div>
        )}

        {!!totalTaxSavings && (
          <div className={styles.chartListItem}>
            <Typography
              className={styles.chartListText}
              variant="bodyS"
              weight="regular"
            >
              {t("calculator_pro_page.analytics.total_tax_savings")}
            </Typography>

            <div className={styles.chartListInner}>
              <Typography
                data-testid={"investorCashFlowAverage"}
                className={cn(styles.chartListValue, {
                  [styles.chartListValuePositive]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE,
                  [styles.chartListValueNeutral]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEUTRAL,
                  [styles.chartListValueNegative]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEGATIVE,
                })}
                variant="numbers"
                weight="medium"
              >
                {convertValueToLocale(Math.abs(totalTaxSavings))}
              </Typography>

              <Popover
                placement="top"
                triggerType="hover"
                yOffset={15}
                floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
                triggerClassName={styles.trigger}
                triggerElement={() => (
                  <InfoIcon className={styles.chartListIcon} />
                )}
              >
                <div className={styles.chartListPopover}>
                  <Typography variant="bodyS" weight="medium">
                    {t(
                      "calculator_pro_page.analytics.total_tax_savings_tooltip"
                    )}
                  </Typography>
                </div>
              </Popover>
            </div>
          </div>
        )}

        {!!specialLoan && (
          <div className={styles.chartListItem}>
            <Typography
              className={styles.chartListText}
              variant="bodyS"
              weight="regular"
            >
              {t("calculator_pro_page.analytics.special_loan")}
            </Typography>

            <div className={styles.chartListInner}>
              <Typography
                data-testid={"specialLoan"}
                className={cn(styles.chartListValue, {
                  [styles.chartListValuePositive]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE,
                  [styles.chartListValueNeutral]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEUTRAL,
                  [styles.chartListValueNegative]:
                    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEGATIVE,
                })}
                variant="numbers"
                weight="medium"
              >
                {convertValueToLocale(specialLoan)}
              </Typography>

              <Popover
                placement="top"
                triggerType="hover"
                yOffset={15}
                floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
                triggerClassName={styles.trigger}
                triggerElement={() => (
                  <InfoIcon className={styles.chartListIcon} />
                )}
              >
                <div className={styles.chartListPopover}>
                  <Typography variant="bodyS" weight="medium">
                    {t("calculator_pro_page.analytics.special_loan_tooltip")}
                  </Typography>
                </div>
              </Popover>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalculatedData;
